import buffer from '@turf/buffer';
import { point, lineString } from '@turf/helpers';
import type { Geometry, Position } from '../types';
import { validateGeometry } from './domain';

export type JagtPoiType =
  | 'skydetaarn'
  | 'fodertoende'
  | 'vildtkamera'
  | 'andet_punkt'
  | 'saltsten'
  | 'jordmaaler'
  | 'faeldesensor';

export type ShootingDirection = 'Alle' | 'N' | 'NØ' | 'Ø' | 'SØ' | 'S' | 'SV' | 'V' | 'NV';

export interface JagtPoiData {
  type: JagtPoiType;
  direction?: ShootingDirection;
  directions?: ShootingDirection[];
  userNotes: string;
}

export interface JagtPoiConfig {
  type: JagtPoiType;
  label: string;
  defaultName: string;
  color: string;
  svgIcon: string;
}

export const SHOOTING_DIRECTIONS: readonly ShootingDirection[] = [
  'Alle',
  'N',
  'NØ',
  'Ø',
  'SØ',
  'S',
  'SV',
  'V',
  'NV',
] as const;

export const DIRECTION_ANGLES: Record<Exclude<ShootingDirection, 'Alle'>, number> = {
  N: 0,
  NØ: 45,
  Ø: 90,
  SØ: 135,
  S: 180,
  SV: 225,
  V: 270,
  NV: 315,
};

export const JAGT_POI_CONFIGS: Record<JagtPoiType, JagtPoiConfig> = {
  skydetaarn: {
    type: 'skydetaarn',
    label: 'Skydetårn / Hochsitz',
    defaultName: 'Skydetårn',
    color: '#d97706',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21 L8 4 L16 4 L20 21"/><path d="M6 15 L18 15"/><path d="M7 10 L17 10"/><rect x="7" y="2" width="10" height="5" rx="1"/></svg>`,
  },
  fodertoende: {
    type: 'fodertoende',
    label: 'Foderplads / Fodertønde',
    defaultName: 'Foderplads',
    color: '#059669',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="7" ry="3"/><path d="M5 5 v10 c0 1.66 3.13 3 7 3 s7 -1.34 7 -3 V5"/><path d="M12 18 v4"/><path d="M9 22 h6"/></svg>`,
  },
  vildtkamera: {
    type: 'vildtkamera',
    label: 'Vildtkamera',
    defaultName: 'Vildtkamera',
    color: '#2563eb',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
  },
  andet_punkt: {
    type: 'andet_punkt',
    label: 'Andet punkt',
    defaultName: 'Punkt',
    color: '#d97706',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
  },
  saltsten: {
    type: 'saltsten',
    label: 'Saltsten / Sliksten',
    defaultName: 'Saltsten',
    color: '#9333ea',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`,
  },
  jordmaaler: {
    type: 'jordmaaler',
    label: 'Jordmåler / Vejrstation',
    defaultName: 'Jordmåler',
    color: '#0284c7',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/><path d="M10 9h4"/><circle cx="12" cy="17" r="1.5" fill="currentColor"/></svg>`,
  },
  faeldesensor: {
    type: 'faeldesensor',
    label: 'Fældesensor (MinkPolice mv.)',
    defaultName: 'Fældesensor',
    color: '#dc2626',
    svgIcon: `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16.01"/><line x1="16" y1="16" x2="16" y2="16.01"/></svg>`,
  },
};

export function parseJagtPoi(notes?: string | null): JagtPoiData | null {
  if (!notes || !notes.trimStart().startsWith('[POI:')) return null;
  const match = notes
    .trimStart()
    .match(/^\[POI:([a-z0-9_-]+)(?:(?::|\|dir:|\|)([a-z0-9æøå,_\s+-]+))?\]\s*([\s\S]*)$/i);
  if (!match) return null;
  const rawType = match[1].toLowerCase();
  if (
    rawType !== 'skydetaarn' &&
    rawType !== 'fodertoende' &&
    rawType !== 'vildtkamera' &&
    rawType !== 'andet_punkt' &&
    rawType !== 'punkt' &&
    rawType !== 'saltsten' &&
    rawType !== 'jordmaaler' &&
    rawType !== 'maaler' &&
    rawType !== 'faeldesensor' &&
    rawType !== 'faelde'
  ) {
    return null;
  }
  const type: JagtPoiType =
    rawType === 'maaler'
      ? 'jordmaaler'
      : rawType === 'faelde'
        ? 'faeldesensor'
        : rawType === 'punkt'
          ? 'andet_punkt'
          : (rawType as JagtPoiType);
  const rawDir = match[2]?.trim();
  const rawParts = rawDir
    ? rawDir
        .split(/[,+\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const directions: ShootingDirection[] = [];
  const validSet = new Set(['Alle', 'N', 'NØ', 'Ø', 'SØ', 'S', 'SV', 'V', 'NV']);
  for (const part of rawParts) {
    const upper = part.toUpperCase();
    if (upper === 'ALLE') {
      directions.push('Alle');
    } else if (validSet.has(upper as ShootingDirection)) {
      directions.push(upper as ShootingDirection);
    }
  }
  const direction: ShootingDirection | undefined =
    directions.length > 0 && !directions.includes('Alle')
      ? (directions.join(', ') as ShootingDirection)
      : undefined;

  return {
    type,
    direction,
    directions,
    userNotes: match[3]?.trim() || '',
  };
}

export const parseJagtPoiNotes = parseJagtPoi;

export function isJagtPoiField(field?: { notes?: string | null } | null): boolean {
  if (!field?.notes) return false;
  return parseJagtPoi(field.notes) !== null || field.notes.trimStart().startsWith('[POI:');
}

export function stripJagtPoiPrefix(notes?: string | null): string {
  if (!notes) return '';
  const parsed = parseJagtPoi(notes);
  if (parsed) return parsed.userNotes;
  return notes.replace(/^\s*\[POI:[^\]]*\]\s*/i, '').trim();
}

export function formatJagtPoiNotes(
  type: JagtPoiType,
  directions?: ShootingDirection | ShootingDirection[],
  userNotes?: string,
): string {
  let dirPart = '';
  if (Array.isArray(directions)) {
    const valid = directions.filter((d) => d && d !== 'Alle');
    if (valid.length > 0) {
      dirPart = `:${valid.join(',')}`;
    }
  } else if (directions && directions !== 'Alle') {
    dirPart = `:${directions}`;
  }
  const notePart = userNotes?.trim() ? ` ${userNotes.trim()}` : '';
  return `[POI:${type}${dirPart}]${notePart}`;
}

export function createShootingSectorCoordinates(
  lat: number,
  lng: number,
  directions: Exclude<ShootingDirection, 'Alle'> | Exclude<ShootingDirection, 'Alle'>[],
  radiusMeters = 40,
): [number, number][][] {
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  const dirArray = Array.isArray(directions) ? directions : [directions];

  return dirArray
    .map((dir) => {
      const bearing = DIRECTION_ANGLES[dir];
      if (bearing === undefined) return [];
      const coords: [number, number][] = [[lat, lng]];
      const startAngle = bearing - 22.5;
      const endAngle = bearing + 22.5;
      for (let step = 0; step <= 4; step++) {
        const angle = startAngle + (step * (endAngle - startAngle)) / 4;
        const angleRad = (angle * Math.PI) / 180;
        const arcLat = lat + (radiusMeters / metersPerDegLat) * Math.cos(angleRad);
        const arcLng = lng + (radiusMeters / metersPerDegLng) * Math.sin(angleRad);
        coords.push([arcLat, arcLng]);
      }
      coords.push([lat, lng]);
      return coords;
    })
    .filter((ring) => ring.length > 0);
}

export function createPoiCircleGeometry(lat: number, lng: number, radiusKm = 0.005): Geometry {
  const pt = point([lng, lat]);
  const buffered = buffer(pt, radiusKm, { units: 'kilometers' });
  if (!buffered || !buffered.geometry) {
    throw new Error('Kunne ikke oprette POI-geometri.');
  }
  return buffered.geometry as Geometry;
}

export function createStripGeometry(coordinates: number[][], widthMeters = 2): Geometry {
  const safeWidth = Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : 2;
  const halfWidthKm = safeWidth / 2 / 1000;
  return createShelterbeltGeometry(coordinates, halfWidthKm);
}

export function createShelterbeltGeometry(coordinates: number[][], bufferKm = 0.003): Geometry {
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    throw new Error('Kunne ikke oprette læhegn-geometri.');
  }

  const validCoords = coordinates.filter(
    (c) =>
      Array.isArray(c) &&
      c.length >= 2 &&
      Number.isFinite(c[0]) &&
      Number.isFinite(c[1]) &&
      Math.abs(c[0]) <= 180 &&
      Math.abs(c[1]) <= 90,
  );
  if (validCoords.length < 2) {
    throw new Error('Kunne ikke oprette læhegn-geometri.');
  }

  const halfWidthKm = Number.isFinite(bufferKm) && bufferKm > 0 ? bufferKm : 0.003;
  const originLng = validCoords[0][0];
  const originLat = validCoords[0][1];
  const cosLat = Math.max(0.1, Math.cos((originLat * Math.PI) / 180));
  const kmPerDegLng = 111.32 * cosLat;
  const kmPerDegLat = 111.32;

  // Convert to local km coordinates and deduplicate points closer than 0.5m
  const pts: Array<{ x: number; y: number }> = [];
  for (const [lng, lat] of validCoords) {
    const x = (lng - originLng) * kmPerDegLng;
    const y = (lat - originLat) * kmPerDegLat;
    const prev = pts[pts.length - 1];
    if (!prev || Math.hypot(x - prev.x, y - prev.y) >= 0.0005) {
      pts.push({ x, y });
    }
  }

  if (pts.length === 1) {
    pts.push({ x: pts[0].x + halfWidthKm * 2, y: pts[0].y });
  }

  const n = pts.length;
  const segNormals: Array<{ nx: number; ny: number; len: number }> = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x;
    const dy = pts[i + 1].y - pts[i].y;
    const len = Math.hypot(dx, dy) || 1e-6;
    segNormals.push({ nx: -dy / len, ny: dx / len, len });
  }

  const leftSide: Position[] = [];
  const rightSide: Position[] = [];

  const toLngLat = (x: number, y: number): Position => [
    Number((originLng + x / kmPerDegLng).toFixed(7)),
    Number((originLat + y / kmPerDegLat).toFixed(7)),
  ];

  for (let i = 0; i < n; i++) {
    let mx: number;
    let my: number;
    if (i === 0) {
      mx = segNormals[0].nx;
      my = segNormals[0].ny;
    } else if (i === n - 1) {
      mx = segNormals[n - 2].nx;
      my = segNormals[n - 2].ny;
    } else {
      const nPrev = segNormals[i - 1];
      const nNext = segNormals[i];
      const sx = nPrev.nx + nNext.nx;
      const sy = nPrev.ny + nNext.ny;
      const sLen = Math.hypot(sx, sy);
      if (sLen < 1e-4) {
        mx = nNext.nx;
        my = nNext.ny;
      } else {
        const bx = sx / sLen;
        const by = sy / sLen;
        const cosHalf = bx * nNext.nx + by * nNext.ny;
        const maxScale = Math.min(
          1.75,
          Math.max(1, (0.45 * Math.min(nPrev.len, nNext.len)) / halfWidthKm),
        );
        const scale = Math.min(maxScale, 1 / Math.max(0.5, Math.abs(cosHalf)));
        mx = bx * scale;
        my = by * scale;
      }
    }

    leftSide.push(toLngLat(pts[i].x + mx * halfWidthKm, pts[i].y + my * halfWidthKm));
    rightSide.push(toLngLat(pts[i].x - mx * halfWidthKm, pts[i].y - my * halfWidthKm));
  }

  const rawRing: Position[] = [...leftSide, ...rightSide.reverse()];
  // Ensure no adjacent duplicate coordinates
  const ring: Position[] = [];
  const seen = new Set<string>();
  for (const pt of rawRing) {
    const key = `${pt[0]},${pt[1]}`;
    if (!seen.has(key)) {
      seen.add(key);
      ring.push(pt);
    }
  }
  if (ring.length >= 3) {
    ring.push([ring[0][0], ring[0][1]]);
    const candidate: Geometry = { type: 'Polygon', coordinates: [ring] };
    if (validateGeometry(candidate) === null) {
      return candidate;
    }
  }

  // Fallback for self-intersecting polylines
  const line = lineString(validCoords);
  const buffered = buffer(line, halfWidthKm, { units: 'kilometers', steps: 4 });
  if (!buffered || !buffered.geometry) {
    throw new Error('Kunne ikke oprette læhegn-geometri.');
  }
  return buffered.geometry as Geometry;
}

/**
 * Beregner det geometriske tyngdepunkt (centroid) for enhver mark eller POI-geometri i Danmark.
 * Returnerer { lat, lng } i WGS84 decimalgrader.
 */
export function getFieldCentroid(
  geometry?: Geometry | { type: string; coordinates: unknown } | null,
): { lat: number; lng: number } | null {
  if (!geometry || !geometry.coordinates) return null;

  const geom = geometry as { type: string; coordinates: any };
  let coords: Position[] = [];

  if (geom.type === 'Polygon' && Array.isArray(geom.coordinates[0])) {
    coords = geom.coordinates[0];
  } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates[0]?.[0])) {
    coords = geom.coordinates[0][0];
  } else if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
    const pt = geom.coordinates as number[];
    if (Number.isFinite(pt[0]) && Number.isFinite(pt[1])) {
      return { lat: pt[1], lng: pt[0] };
    }
    return null;
  }

  if (!coords.length) return null;

  let sumLng = 0;
  let sumLat = 0;
  let count = 0;

  for (const c of coords) {
    if (Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
      sumLng += c[0];
      sumLat += c[1];
      count++;
    }
  }

  if (count === 0) return null;
  return { lat: sumLat / count, lng: sumLng / count };
}

/**
 * Formaterer et sæt koordinater brugervenligt og præcist (4 decimaler, ca. 11 meter).
 */
export function formatGpsCoords(lat: number, lng: number): string {
  return `${lat.toFixed(4)}° N, ${lng.toFixed(4)}° Ø`;
}
